import { useState, useCallback, useRef, useEffect } from 'react';

import axios, { type AxiosProgressEvent, type CancelTokenSource } from 'axios';

import { isAllowed } from '@/components/globals/MimeTypes';
import { MAX_UPLOADS } from '@/env';
import { hashFile } from '@/lib/hashFile';
import { trpc } from '@/lib/trpc';
import useProductStore from '@/stores/productStore';
import { stepStore } from '@/stores/steps';

type UploadStatus = 'idle' | 'uploading' | 'done' | 'error' | 'aborted' | 'exists' | 'hashing';

export interface UploadItem {
  id: string;
  deliverableId?: string;
  file: File;
  progress: number;
  hash: string;
  status: UploadStatus;
  url?: string;
  size?: number;
}

export function useUploader({
  onUploadComplete,
  onUploadDelete
}: {
  onUploadComplete: (data: {
    hash: string;
    id: string;
    filename: string;
    deliverableId?: string;
    size: number;
  }) => void;
  onUploadDelete: (id: string, filename: string) => void;
}) {
  const uploadingCountRef = useRef(0);
  const currentDeliverables = useProductStore(d => d.deliverables);
  const [uploads, setUploads] = useState<UploadItem[]>(
    currentDeliverables.map(d => {
      return {
        id: d.id,
        file: new File([], d.filename),
        progress: 100,
        size: d.size,
        status: 'done' as UploadStatus,
        hash: d.hash,
        url: ''
      };
    })
  );

  const cancelTokens = useRef<Map<string, CancelTokenSource>>(new Map());

  useEffect(() => {
    stepStore.getState().setLocked(uploads.some(u => u.status === 'uploading' || u.status === 'hashing'));
  }, [uploads]);

  const getSignedUrl = trpc.files.getPresignedUrl.useMutation();
  const createUploadedDeliverable = trpc.deliverables.createFromUploadedFile.useMutation();

  const startUpload = useCallback(
    async (id: string, hash: string, file: File) => {
      const cancelToken = axios.CancelToken.source();
      cancelTokens.current.set(id, cancelToken);

      try {
        const allowedFileType = isAllowed(file.type, file.name);

        if (!allowedFileType) {
          setUploads(prev => prev.map(u => (u.id === id ? { ...u, status: 'error' } : u)));

          return;
        }

        const {
          uploadUrl,
          fileUrl,
          path,
          id: deliverableId
        } = await getSignedUrl.mutateAsync({
          fileName: file.name,
          fileType: file.type,
          hash
        });

        if (!uploadUrl) {
          onUploadComplete({
            hash,
            id,
            deliverableId,
            filename: file.name,
            size: file.size
          });

          setUploads(prev =>
            prev.map(u =>
              u.id === id ? { ...u, progress: 100, status: 'exists', url: fileUrl, deliverableId, size: file.size } : u
            )
          );

          return;
        }

        await axios.put(uploadUrl, file, {
          cancelToken: cancelToken.token,
          onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            if (progressEvent.total) {
              const progress = (progressEvent.loaded / progressEvent.total) * 100;
              setUploads(prev => prev.map(u => (u.id === id ? { ...u, progress } : u)));
            }
          }
        });

        const result = await createUploadedDeliverable.mutateAsync({
          hash,
          path,
          size: file.size,
          name: file.name,
          type: file.type
        });

        onUploadComplete({
          hash,
          deliverableId: result.id,
          id,
          filename: file.name,
          size: result.size
        });

        setUploads(prev =>
          prev.map(u =>
            u.id === id
              ? { ...u, progress: 100, status: 'done', url: fileUrl, deliverableId: result.id, size: result.size }
              : u
          )
        );
      } catch (error: any) {
        if (axios.isCancel(error)) {
          setUploads(prev => prev.map(u => (u.id === id ? { ...u, status: 'aborted' } : u)));
          return;
        }

        setUploads(prev => prev.map(u => (u.id === id ? { ...u, status: 'error' } : u)));
      }
    },
    [getSignedUrl, onUploadComplete, createUploadedDeliverable]
  );

  const deleteUpload = useCallback(
    (id: string, filename: string) => {
      setUploads(prev => prev.filter(u => u.deliverableId !== id && u.id !== id));
      onUploadDelete(id, filename);
    },
    [onUploadDelete]
  );

  const addFile = useCallback(
    async (file: File) => {
      if (uploadingCountRef.current >= MAX_UPLOADS) {
        return;
      }

      uploadingCountRef.current += 1;

      const id = crypto.randomUUID();

      setUploads(prev => [...prev, { id, file, progress: 0, status: 'hashing', hash: '' }]);

      const hash = await hashFile(file);

      const exists = uploads.find(u => u.hash === hash);

      if (exists) {
        setUploads(prev => prev.filter(u => u.id !== id));
        return;
      }

      setUploads(prev => prev.map(u => (u.id === id ? { ...u, progress: 0, status: 'uploading', hash } : u)));

      startUpload(id, hash, file);
    },
    [startUpload, uploads, setUploads]
  );

  const retry = useCallback(
    (id: string) => {
      const upload = uploads.find(u => u.id === id);

      if (upload) {
        setUploads(prev => prev.map(u => (u.id === id ? { ...u, status: 'uploading', progress: 0 } : u)));
        startUpload(id, upload.hash, upload.file);
      }
    },
    [uploads, startUpload]
  );

  const deleteCancelToken = useCallback((id: string) => {
    const cancelToken = cancelTokens.current.get(id);

    if (cancelToken) {
      cancelToken.cancel('Upload aborted');
      cancelTokens.current.delete(id);
    }
  }, []);

  const abort = useCallback(
    (id: string, filename: string) => {
      uploadingCountRef.current -= 1;

      setUploads(prev =>
        prev.map(u => {
          if (u.id !== id) {
            return u;
          }

          if (['done', 'exists', 'aborted'].includes(u.status)) {
            return u;
          }

          deleteCancelToken(u.id);

          return { ...u, status: 'aborted' };
        })
      );
      deleteUpload(id, filename);
      onUploadDelete(id, filename);
    },
    [deleteCancelToken, deleteUpload, onUploadDelete]
  );

  const done = uploads.length > 0 && uploads.every(u => ['done', 'aborted', 'exists'].includes(u.status));
  const isFull = uploads.length >= MAX_UPLOADS;

  return { addFile, retry, uploads, done, abort, isFull };
}
